import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { t } from '../i18n'
import { flagsQuery } from '../lib/queries'
import { haptic } from '../lib/telegram'

type Tab = {
  to: string
  label: string
  icon: ReactNode
}

const TABS: Tab[] = [
  {
    to: '/',
    label: t.nav.today,
    icon: (
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8.4v3.9l2.6 1.6" />
      </svg>
    ),
  },
  {
    to: '/week',
    label: t.nav.week,
    icon: (
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        aria-hidden
      >
        <path d="M4 6.5h16M4 12h16M4 17.5h10" />
      </svg>
    ),
  },
  {
    to: '/products',
    label: t.nav.products,
    icon: (
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M9.5 3.5h5v3l1.8 3.2V20H7.7V9.7L9.5 6.5z" />
        <path d="M7.7 13.8h8.6" />
      </svg>
    ),
  },
  {
    to: '/spf',
    label: 'SPF',
    icon: (
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="3.6" />
        <path d="M12 4.2V2.6M12 21.4v-1.6M4.2 12H2.6M21.4 12h-1.6M6.7 6.7L5.6 5.6M18.4 18.4l-1.1-1.1M17.3 6.7l1.1-1.1M5.6 18.4l1.1-1.1" />
      </svg>
    ),
  },
  {
    to: '/more',
    label: t.nav.more,
    icon: (
      <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <circle cx="6" cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="18" cy="12" r="1.7" />
      </svg>
    ),
  },
]

function activeTab(path: string): number {
  const index = TABS.findIndex((tab) => (tab.to === '/' ? path === '/' : path.startsWith(tab.to)))
  return index === -1 ? TABS.length - 1 : index
}

export function Nav() {
  const path = useRouterState({ select: (state) => state.location.pathname })
  const activeIndex = activeTab(path)
  const { data: flags } = useQuery(flagsQuery())
  const alerting = flags?.some((flag) => flag.severity === 'alert') ?? false

  return (
    <>
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-10 h-(--nav-scrim-height)"
        style={{ background: 'linear-gradient(to top, var(--nav-scrim), transparent)' }}
        aria-hidden
      />
      <nav className="fixed inset-x-0 bottom-[calc(var(--spacing-safe-bottom)+(var(--spacing-nav-gap)))] z-20 flex justify-center px-4">
        <div className="relative flex rounded-capsule border border-nav bg-nav p-1 shadow-nav backdrop-blur-3">
          <span
            className="absolute bottom-1 top-1 w-[62px] rounded-capsule bg-nav-pill transition-[left] duration-thumb ease-sheet"
            style={{
              left: `${4 + Math.max(activeIndex, 0) * 62}px`,
              boxShadow: 'var(--nav-pill-shadow)',
            }}
            aria-hidden
          />
          {TABS.map((tab, index) => (
            <Link
              key={tab.to}
              to={tab.to}
              onClick={() => haptic.select()}
              className={`relative z-10 flex min-h-[52px] w-[62px] flex-col items-center gap-[3px] px-0 pb-2 pt-[6px] ${
                index === activeIndex ? 'text-accent-deep' : 'text-text-muted'
              }`}
            >
              {tab.icon}
              {alerting && tab.to === '/more' ? (
                <span
                  className="absolute right-[15px] top-[7px] h-[7px] w-[7px] rounded-full border border-nav"
                  style={{ background: 'var(--danger)' }}
                  aria-hidden
                />
              ) : null}
              <span className="text-note font-medium leading-[13px]">{tab.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </>
  )
}
